<template>
  <v-container fluid>
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="dashboard-header">
          <h1 class="dashboard-title">
            <v-icon large color="primary" class="mr-3">mdi-school</v-icon>
            ระบบข้อมูลนักเรียน
          </h1>
          <p class="dashboard-subtitle">
            ข้อมูล ณ วันที่ {{ currentDate }} | ปีการศึกษา {{ year_s }}
          </p>
        </div>
      </v-col>
    </v-row>

    <!-- Summary Cards -->
    <v-row class="mb-6">
      <v-col cols="12" md="3" v-for="(card, index) in summaryCards" :key="index">
        <v-card class="summary-card" elevation="8" :class="card.color">
          <v-card-text class="text-center">
            <v-icon :color="card.iconColor" size="48" class="mb-3">
              {{ card.icon }}
            </v-icon>
            <h3 class="card-title">{{ card.title }}</h3>
            <p class="card-value">{{ card.value }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Summary by Year Table -->
    <base-material-card
      icon="mdi-chart-line"
      title="สรุปจำนวนนักเรียนรายปี"
      class="mb-6 enhanced-card"
    >
      <template v-slot:after-heading>
        <v-chip color="primary" outlined class="ml-2">
          <v-icon left small>mdi-calendar</v-icon>
          อัพเดทล่าสุด: {{ currentDate }}
        </v-chip>
      </template>
      
      <v-card flat class="search-card mb-4">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="searchSummary"
                append-icon="mdi-magnify"
                label="ค้นหาข้อมูลสรุป..."
                single-line
                hide-details
                outlined
                dense
                clearable
                class="search-field"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="primary">mdi-database-search</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="text-right">
              <v-btn
                color="primary"
                outlined
                @click="exportSummaryData"
                class="mr-2"
              >
                <v-icon left>mdi-download</v-icon>
                ส่งออกข้อมูล
              </v-btn>
              <v-btn
                color="success"
                @click="refreshSummaryData"
                :loading="loading"
              >
                <v-icon left>mdi-refresh</v-icon>
                รีเฟรช
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-data-table
        color="primary"
        :loading="loading"
        :headers="headersummary"
        :items="rate_work_college_summarys"
        :search="searchSummary"
        class="enhanced-table"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-text': 'แถวต่อหน้า:',
          'items-per-page-options': [5, 10, 15, 20, -1]
        }"
      >
        <template v-slot:item.rate_work_college_year="{ item }">
          <v-chip color="secondary" outlined>
            <v-icon left small>mdi-calendar</v-icon>
            {{ item.rate_work_college_year }}
          </v-chip>
        </template>

        <template v-slot:item.sumrate_work_field_study_lavel_1="{ item }">
          <v-chip color="blue" outlined>
            {{ item.sumrate_work_field_study_lavel_1 || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.sumrate_work_field_study_lavel_2="{ item }">
          <v-chip color="green" outlined>
            {{ item.sumrate_work_field_study_lavel_2 || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.sumrate_work_field_study_lavel_3="{ item }">
          <v-chip color="orange" outlined>
            {{ item.sumrate_work_field_study_lavel_3 || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.sumrate_work_field_study_lavel_all="{ item }">
          <v-chip color="success" dark>
            <strong>{{ item.sumrate_work_field_study_lavel_all || 0 }}</strong>
          </v-chip>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon size="64" color="grey">mdi-database-off</v-icon>
            <p class="mt-4 grey--text">ไม่พบข้อมูล</p>
          </div>
        </template>

        <template v-slot:no-results>
          <v-alert type="warning" outlined class="ma-4">
            <v-icon slot="prepend">mdi-alert</v-icon>
            ไม่พบผลลัพธ์ที่ตรงกับ "{{ searchSummary }}"
          </v-alert>
        </template>
      </v-data-table>
    </base-material-card>

    <!-- Detailed Data Table -->
    <base-material-card
      icon="mdi-table-large"
      :title="`ข้อมูลรายละเอียดนักเรียน ปีการศึกษา ${year_s}`"
      class="enhanced-card"
    >
      <template v-slot:after-heading>
        <v-chip color="info" outlined class="ml-2">
          <v-icon left small>mdi-account-group</v-icon>
          ทั้งหมด {{ rate_work_college_sums.length }} รายการ
        </v-chip>
      </template>

      <v-card flat class="search-card mb-4">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="searchDetail"
                append-icon="mdi-magnify"
                label="ค้นหาข้อมูลรายละเอียด..."
                single-line
                hide-details
                outlined
                dense
                clearable
                class="search-field"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="primary">mdi-account-search</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="selectedLevel"
                :items="levelOptions"
                label="กรองตามระดับ"
                outlined
                dense
                clearable
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon color="primary">mdi-filter</v-icon>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="3" class="text-right">
              <v-btn
                color="primary"
                outlined
                @click="exportDetailData"
                class="mr-2"
              >
                <v-icon left>mdi-download</v-icon>
                ส่งออก
              </v-btn>
              <v-btn
                color="success"
                @click="refreshDetailData"
                :loading="loading"
              >
                <v-icon left>mdi-refresh</v-icon>
                รีเฟรช
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-data-table
        color="primary"
        :loading="loading"
        :headers="headers"
        :items="filteredDetailData"
        :search="searchDetail"
        class="enhanced-table"
        :items-per-page="15"
        :footer-props="{
          'items-per-page-text': 'แถวต่อหน้า:',
          'items-per-page-options': [10, 15, 25, 50, -1]
        }"
      >
        <template v-slot:item.rate_work_college_id_class="{ item }">
          <v-chip 
            :color="getLevelColor(item.rate_work_college_id_class)" 
            dark
            small
          >
            <v-icon left small>{{ getLevelIcon(item.rate_work_college_id_class) }}</v-icon>
            {{ getLevelText(item.rate_work_college_id_class) }}
          </v-chip>
        </template>

        <template v-slot:item.subject_typeName="{ item }">
          <div class="subject-cell">
            <v-icon color="primary" class="mr-2">mdi-book-open-variant</v-icon>
            {{ item.subject_typeName || 'ไม่ระบุ' }}
          </div>
        </template>

        <template v-slot:item.sumrate_work_field_study_lavel_1="{ item }">
          <v-chip color="blue" outlined>
            {{ item.sumrate_work_field_study_lavel_1 || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.sumrate_work_field_study_lavel_2="{ item }">
          <v-chip color="green" outlined>
            {{ item.sumrate_work_field_study_lavel_2 || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.sumrate_work_field_study_lavel_3="{ item }">
          <v-chip color="orange" outlined>
            {{ item.sumrate_work_field_study_lavel_3 || 0 }}
          </v-chip>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon size="64" color="grey">mdi-database-off</v-icon>
            <p class="mt-4 grey--text">ไม่พบข้อมูล</p>
          </div>
        </template>

        <template v-slot:no-results>
          <v-alert type="warning" outlined class="ma-4">
            <v-icon slot="prepend">mdi-alert</v-icon>
            ไม่พบผลลัพธ์ที่ตรงกับ "{{ searchDetail }}"
          </v-alert>
        </template>
      </v-data-table>
    </base-material-card>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" opacity="0.8">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
          class="mb-4"
        ></v-progress-circular>
        <p class="white--text">กำลังโหลดข้อมูล...</p>
      </div>
    </v-overlay>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: "HRvecStdsummary",

  data() {
    return {
      ApiKey: "HRvec2021",
      headers: [
        { 
          text: "ประเภทวิชา", 
          align: "left", 
          value: "subject_typeName",
          sortable: true,
          width: "250px"
        },
        { 
          text: "ระดับ", 
          align: "center", 
          value: "rate_work_college_id_class",
          sortable: true,
          width: "120px"
        },
        {
          text: "ปวช.1/ปวส.1",
          align: "center",
          value: "sumrate_work_field_study_lavel_1",
          sortable: true,
          width: "130px"
        },
        {
          text: "ปวช.2/ปวส.2",
          align: "center",
          value: "sumrate_work_field_study_lavel_2",
          sortable: true,
          width: "130px"
        },
        {
          text: "ปวช.3",
          align: "center",
          value: "sumrate_work_field_study_lavel_3",
          sortable: true,
          width: "130px"
        }
      ],
      headersummary: [
        { 
          text: "ปีการศึกษา", 
          align: "center", 
          value: "rate_work_college_year",
          sortable: true,
          width: "150px"
        },
        {
          text: "ปวช.1/ปวส.1",
          align: "center",
          value: "sumrate_work_field_study_lavel_1",
          sortable: true,
          width: "130px"
        },
        {
          text: "ปวช.2/ปวส.2",
          align: "center",
          value: "sumrate_work_field_study_lavel_2",
          sortable: true,
          width: "130px"
        },
        {
          text: "ปวช.3",
          align: "center",
          value: "sumrate_work_field_study_lavel_3",
          sortable: true,
          width: "130px"
        },
        {
          text: "รวมทั้งหมด",
          align: "center",
          value: "sumrate_work_field_study_lavel_all",
          sortable: true,
          width: "150px"
        }
      ],
      searchSummary: "",
      searchDetail: "",
      selectedLevel: null,
      levelOptions: [
        { text: "ปวช.", value: "20" },
        { text: "ปวส.", value: "30" },
        { text: "ทล.บ", value: "40" }
      ],
      rate_work_college_sums: [],
      rate_work_college_summarys: [],
      loading: false,
      snackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000
      }
    };
  },

  async mounted() {
    await this.loadInitialData();
  },

  computed: {
    year_s() {
      const d = new Date();
      let year = d.getFullYear() + 543;
      return year;
    },

    currentDate() {
      const today = new Date();
      const day = today.getDate();
      const month = today.toLocaleString('th-TH', { month: 'long' });
      const year = today.getFullYear() + 543;
      return `${day} ${month} ${year}`;
    },

    summaryCards() {
      const summaryData = this.rate_work_college_summarys;
      
      // ตรวจสอบว่ามีข้อมูลหรือไม่
      if (!summaryData || summaryData.length === 0) {
        return [
          {
            title: "ปวช.1/ปวส.1",
            value: 0,
            icon: "mdi-account-group",
            color: "blue-card",
            iconColor: "blue"
          },
          {
            title: "ปวช.2/ปวส.2",
            value: 0,
            icon: "mdi-account-multiple",
            color: "green-card",
            iconColor: "green"
          },
          {
            title: "ปวช.3",
            value: 0,
            icon: "mdi-account-star",
            color: "orange-card",
            iconColor: "orange"
          },
          {
            title: "รวมทั้งหมด",
            value: 0,
            icon: "mdi-account-supervisor",
            color: "purple-card",
            iconColor: "purple"
          }
        ];
      }

      // หาข้อมูลของปีปัจจุบัน
      const currentYearData = summaryData.find(item => 
        item.rate_work_college_year === this.year_s.toString()
      );

      // ถ้าไม่พบข้อมูลของปีปัจจุบัน ให้ใช้ข้อมูลของปีล่าสุด
      const dataToUse = currentYearData || summaryData[0] || {};

      return [
        {
          title: "ปวช.1/ปวส.1",
          value: this.safeGetNumber(dataToUse.sumrate_work_field_study_lavel_1),
          icon: "mdi-account-group",
          color: "blue-card",
          iconColor: "blue"
        },
        {
          title: "ปวช.2/ปวส.2",
          value: this.safeGetNumber(dataToUse.sumrate_work_field_study_lavel_2),
          icon: "mdi-account-multiple",
          color: "green-card",
          iconColor: "green"
        },
        {
          title: "ปวช.3",
          value: this.safeGetNumber(dataToUse.sumrate_work_field_study_lavel_3),
          icon: "mdi-account-star",
          color: "orange-card",
          iconColor: "orange"
        },
        {
          title: "รวมทั้งหมด",
          value: this.safeGetNumber(dataToUse.sumrate_work_field_study_lavel_all),
          icon: "mdi-account-supervisor",
          color: "purple-card",
          iconColor: "purple"
        }
      ];
    },

    filteredDetailData() {
      if (!this.selectedLevel) {
        return this.rate_work_college_sums;
      }
      return this.rate_work_college_sums.filter(item => 
        item.rate_work_college_id_class === this.selectedLevel
      );
    }
  },

  methods: {
    // Helper method สำหรับการเช็คและแปลงค่าเป็นตัวเลข
    safeGetNumber(value) {
      if (value === null || value === undefined || value === '') {
        return 0;
      }
      const num = parseInt(value);
      return isNaN(num) ? 0 : num;
    },

    async loadInitialData() {
      this.loading = true;
      try {
        await Promise.all([
          this.rate_work_college_sumQuery(),
          this.rate_work_college_summaryQuery()
        ]);
        this.showSnackbar("โหลดข้อมูลสำเร็จ", "success");
      } catch (error) {
        console.error("Error loading initial data:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
      } finally {
        this.loading = false;
      }
    },

    async rate_work_college_sumQuery() {
      try {
        let result = await this.$http.post("rate_work_college_sum.php", {
          ApiKey: this.ApiKey,
          sumtypesubject: "ok",
          rate_work_college_year: this.year_s
        });
        
        // ตรวจสอบและกรองข้อมูล
        if (result && result.data) {
          this.rate_work_college_sums = result.data.map(item => ({
            ...item,
            sumrate_work_field_study_lavel_1: this.safeGetNumber(item.sumrate_work_field_study_lavel_1),
            sumrate_work_field_study_lavel_2: this.safeGetNumber(item.sumrate_work_field_study_lavel_2),
            sumrate_work_field_study_lavel_3: this.safeGetNumber(item.sumrate_work_field_study_lavel_3),
            subject_typeName: item.subject_typeName || 'ไม่ระบุ'
          }));
        } else {
          this.rate_work_college_sums = [];
        }
      } catch (error) {
        console.error("Error fetching detail data:", error);
        this.rate_work_college_sums = [];
        throw error;
      }
    },

    async rate_work_college_summaryQuery() {
      try {
        let result = await this.$http.post("rate_work_college_sum.php", {
          ApiKey: this.ApiKey
        });
        
        // ตรวจสอบและกรองข้อมูล
        if (result && result.data) {
          this.rate_work_college_summarys = result.data.map(item => ({
            ...item,
            sumrate_work_field_study_lavel_1: this.safeGetNumber(item.sumrate_work_field_study_lavel_1),
            sumrate_work_field_study_lavel_2: this.safeGetNumber(item.sumrate_work_field_study_lavel_2),
            sumrate_work_field_study_lavel_3: this.safeGetNumber(item.sumrate_work_field_study_lavel_3),
            sumrate_work_field_study_lavel_all: this.safeGetNumber(item.sumrate_work_field_study_lavel_all)
          }));
        } else {
          this.rate_work_college_summarys = [];
        }
      } catch (error) {
        console.error("Error fetching summary data:", error);
        this.rate_work_college_summarys = [];
        throw error;
      }
    },

    async refreshSummaryData() {
      this.loading = true;
      try {
        await this.rate_work_college_summaryQuery();
        this.showSnackbar("รีเฟรชข้อมูลสรุปสำเร็จ", "success");
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการรีเฟรชข้อมูล", "error");
      } finally {
        this.loading = false;
      }
    },

    async refreshDetailData() {
      this.loading = true;
      try {
        await this.rate_work_college_sumQuery();
        this.showSnackbar("รีเฟรชข้อมูลรายละเอียดสำเร็จ", "success");
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการรีเฟรชข้อมูล", "error");
      } finally {
        this.loading = false;
      }
    },

    exportSummaryData() {
      try {
        // เตรียมข้อมูลสำหรับส่งออก
        const exportData = this.rate_work_college_summarys.map(item => ({
          'ปีการศึกษา': item.rate_work_college_year,
          'ปวช.1/ปวส.1': item.sumrate_work_field_study_lavel_1,
          'ปวช.2/ปวส.2': item.sumrate_work_field_study_lavel_2,
          'ปวช.3': item.sumrate_work_field_study_lavel_3,
          'รวมทั้งหมด': item.sumrate_work_field_study_lavel_all
        }));

        // สร้างไฟล์ CSV
        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map(row => 
            headers.map(header => `"${row[header]}"`).join(',')
          )
        ].join('\n');

        // ดาวน์โหลดไฟล์
        const blob = new Blob(['\ufeff' + csvContent], { 
          type: 'text/csv;charset=utf-8;' 
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `student_summary_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.showSnackbar("ส่งออกข้อมูลสำเร็จ", "success");
      } catch (error) {
        console.error("Export error:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการส่งออกข้อมูล", "error");
      }
    },

    exportDetailData() {
      try {
        // เตรียมข้อมูลสำหรับส่งออก
        const exportData = this.filteredDetailData.map(item => ({
          'ประเภทวิชา': item.subject_typeName,
          'ระดับ': this.getLevelText(item.rate_work_college_id_class),
          'ปวช.1/ปวส.1': item.sumrate_work_field_study_lavel_1,
          'ปวช.2/ปวส.2': item.sumrate_work_field_study_lavel_2,
          'ปวช.3': item.sumrate_work_field_study_lavel_3
        }));

        // สร้างไฟล์ CSV
        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map(row => 
            headers.map(header => `"${row[header]}"`).join(',')
          )
        ].join('\n');

        // ดาวน์โหลดไฟล์
        const blob = new Blob(['\ufeff' + csvContent], { 
          type: 'text/csv;charset=utf-8;' 
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `student_details_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.showSnackbar("ส่งออกข้อมูลสำเร็จ", "success");
      } catch (error) {
        console.error("Export error:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการส่งออกข้อมูล", "error");
      }
    },

    getLevelText(level) {
      switch(level) {
        case '20': return 'ปวช.';
        case '30': return 'ปวส.';
        case '40': return 'ทล.บ';
        default: return level || 'ไม่ระบุ';
      }
    },

    getLevelColor(level) {
      switch(level) {
        case '20': return 'blue';
        case '30': return 'green';
        case '40': return 'orange';
        default: return 'grey';
      }
    },

    getLevelIcon(level) {
      switch(level) {
        case '20': return 'mdi-school';
        case '30': return 'mdi-school-outline';
        case '40': return 'mdi-university';
        default: return 'mdi-help';
      }
    },

    showSnackbar(text, color = "success", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.student-dashboard {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 20px;
}

.dashboard-header {
  text-align: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  color: white;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  
  .dashboard-title {
    font-size: 2.5rem;
    font-weight: 600;
    margin-bottom: 10px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  }
  
  .dashboard-subtitle {
    font-size: 1.2rem;
    opacity: 0.9;
    margin: 0;
  }
}

.summary-card {
  border-radius: 16px !important;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0,0,0,0.1) !important;
  }
  
  .card-title {
    font-size: 1.1rem;
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 8px;
  }
  
  .card-value {
    font-size: 2rem;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
  }
}

.blue-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-left: 4px solid #2196f3;
}

.green-card {
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
}

.orange-card {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-left: 4px solid #ff9800;
}

.purple-card {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  border-left: 4px solid #9c27b0;
}

.enhanced-card {
  border-radius: 16px !important;
  box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
  overflow: hidden;
  
  ::v-deep .v-card__title {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e9ecef;
  }
}

.search-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
  border-radius: 12px !important;
  border: 1px solid #e9ecef;
}

.search-field {
  ::v-deep .v-input__control {
    .v-input__slot {
      background: white !important;
      border-radius: 8px !important;
    }
  }
}

.enhanced-table {
  border-radius: 12px !important;
  overflow: hidden;
  
  ::v-deep .v-data-table__wrapper {
    border-radius: 12px;
  }
  
  ::v-deep .v-data-table-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    
    th {
      font-weight: 600 !important;
      color: #495057 !important;
      border-bottom: 2px solid #dee2e6 !important;
    }
  }
  
  ::v-deep tbody tr {
    transition: all 0.2s ease;
    
    &:hover {
      background: #f8f9fa !important;
      transform: scale(1.01);
    }
  }
}

.subject-cell {
  display: flex;
  align-items: center;
  font-weight: 500;
}

// Custom scrollbar
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

// Animation for loading
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.v-data-table--loading {
  animation: pulse 1.5s infinite;
}

// Mobile responsiveness
@media (max-width: 768px) {
  .dashboard-header {
    padding: 30px 15px;
    
    .dashboard-title {
      font-size: 2rem;
    }
    
    .dashboard-subtitle {
      font-size: 1rem;
    }
  }
  
  .summary-card {
    margin-bottom: 15px;
  }
  
  .v-data-table {
    font-size: 0.875rem;
  }
}

// Error handling styles
.error-state {
  text-align: center;
  padding: 40px;
  color: #666;
  
  .error-icon {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  .error-message {
    font-size: 1.2rem;
    margin-bottom: 16px;
  }
  
  .error-description {
    font-size: 1rem;
    opacity: 0.7;
  }
}

// Loading state styles
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  
  .loading-spinner {
    margin-bottom: 16px;
  }
  
  .loading-text {
    font-size: 1.1rem;
    color: #666;
  }
}

// Enhanced chip styles
.v-chip {
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
}

// Table row animation
@keyframes tableRowSlide {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.enhanced-table ::v-deep tbody tr {
  animation: tableRowSlide 0.3s ease-out;
}

// Snackbar custom styles
.v-snackbar {
  ::v-deep .v-snack__wrapper {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
}

// Card hover effects
.summary-card:hover {
  .card-value {
    color: #1976d2;
  }
}

// Search field focus effects
.search-field:focus-within {
  ::v-deep .v-input__slot {
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
  }
}

// Button hover effects
.v-btn {
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
}

// Data table header improvements
.enhanced-table ::v-deep .v-data-table-header th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
}

// Responsive table improvements
@media (max-width: 600px) {
  .enhanced-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
    
    table {
      min-width: 600px;
    }
  }
}

// Print styles
@media print {
  .dashboard-header {
    background: white !important;
    color: black !important;
    box-shadow: none !important;
  }
  
  .summary-card {
    background: white !important;
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }
  
  .enhanced-table {
    box-shadow: none !important;
  }
  
  .v-btn,
  .v-overlay,
  .v-snackbar {
    display: none !important;
  }
}
</style>